import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import debounce from 'lodash.debounce';
import { AuthHooks, GoodHooks, UserHooks } from 'common/hooks';
import { getPriceToDisplay, priceFormatter } from 'common/helpers';
import { Counter, DeliveryBadge, Divider, IconBackground, Image } from 'common/components';
import { Contract, Email, InfoYellow } from 'common/svg';
import { ITEMS_FOR_VIEW_LIMIT_4 } from 'common/config';
import { EColor } from 'common/const';
import { useAppSelector } from 'app/store/store.hooks';
import { IBasket, IBasketGoods, IUpdateBasketPayload } from 'entities/Basket/Basket.models';
import { mapBasketGoodsToPayload } from 'entities/Basket/Basket.helper';
import { IContractGoodsSettingsItem } from 'entities/Contract/Contract.models';
import { IGood } from 'entities/Goods/Goods.models';
import { GoodDiscount, GoodInStock, GoodsAvailabilitySettingsModal } from 'entities/Goods/components';
import { selectCurrentUser } from 'entities/User/User.slice';
import { selectCurrentBasket } from 'entities/Basket/Basket.slice';
import { selectXBasketId } from 'entities/Auth/Auth.slice';

interface IComponentProps {
  good: IGood | null;
  loading: boolean;
  settings: IContractGoodsSettingsItem[];
  onAddToBasketClick: (id: number, count: number) => void;
  onNavigateToBasketClick: () => void;
  onBasketUpdate: (payload: IUpdateBasketPayload) => Promise<IBasket | null>;
  onSendRequestClick: () => Promise<void>;
}

export const GoodSidebar: FC<IComponentProps> = ({
  good,
  loading,
  settings,
  onAddToBasketClick,
  onNavigateToBasketClick,
  onBasketUpdate,
  onSendRequestClick,
}) => {
  // auth
  const xBasketId = useAppSelector(selectXBasketId);
  // basket
  const currentBasket = useAppSelector(selectCurrentBasket);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [count, setCount] = useState<number>(1);
  const [countIsChanged, setCountIsChanged] = useState<boolean>(false);
  const [updatingGoodsCountRequestsCount, setUpdatingGoodsCountRequestsCount] = useState<number>(0);
  const [requestLoading, setRequestLoading] = useState<boolean>(false);
  const [openSettings, setOpenSettings] = useState<boolean>(false);
  const [settingsItem, setSettingsItem] = useState<IContractGoodsSettingsItem | null>(null);
  const [openGoodsAvailabilitySettingsModal, setOpenGoodsAvailabilitySettingsModal] = useState<boolean>(false);
  const { isSeller } = AuthHooks.useIsSeller();
  const { isAccountAdmin, isSellerManager } = UserHooks.useCurrentUserRole();
  const { isSelfRegistered } = UserHooks.useIsSelfRegistered();
  const { getGoodInBasket } = GoodHooks.useGoodInBasket();

  const isCustomer = !isSeller || !!xBasketId;
  const isUpdatingGoodsLoading = countIsChanged || updatingGoodsCountRequestsCount > 0;
  const goodInBasket = good ? getGoodInBasket(good.id) : false;

  const priceToDisplay = good ? getPriceToDisplay(good.price, good.priceWithTaxes, currentUser?.account) : 0;
  const originalPriceToDisplay = good
    ? getPriceToDisplay(good.originalPrice, good.originalPriceWithTaxes, currentUser?.account)
    : 0;
  const diff = originalPriceToDisplay - priceToDisplay;

  const formattedDiff = priceFormatter(diff);
  const formattedPriceToDisplay = priceFormatter(priceToDisplay);
  const formattedOriginalPriceToDisplay = priceFormatter(originalPriceToDisplay);
  const formattedPriceWithTaxes = priceFormatter(good?.priceWithTaxes);

  useEffect(() => {
    if (!good) {
      return;
    }

    const basketGood = currentBasket?.goods.find((item) => item.goodId === good.id);

    if (basketGood) {
      setCount(basketGood.count);
    }
  }, [good]);

  const debouncedUpdateBasketGoods = useMemo(
    () =>
      debounce(async (goods: IBasketGoods[]) => {
        setUpdatingGoodsCountRequestsCount((count) => count + 1);

        try {
          if (!currentBasket) {
            return;
          }

          await onBasketUpdate({
            id: currentBasket.id,
            goods: mapBasketGoodsToPayload(goods),
          });
        } finally {
          setCountIsChanged(false);
          setUpdatingGoodsCountRequestsCount((count) => count - 1);
        }
      }, 800),
    [currentBasket],
  );

  useEffect(() => {
    return () => {
      debouncedUpdateBasketGoods.cancel();
    };
  }, [debouncedUpdateBasketGoods]);

  const changeCount = (value: number): void => {
    setCount(value);

    if (good && currentBasket && goodInBasket) {
      setCountIsChanged(true);

      const newBasketGoods = currentBasket.goods.map((item) => {
        if (item.goodId === good.id) {
          return {
            ...item,
            count: value,
          };
        }

        return item;
      });

      debouncedUpdateBasketGoods(newBasketGoods);
    }
  };

  const handleAdToBasket = (): void => {
    if (!good) {
      return;
    }

    onAddToBasketClick(good.id, count);
  };

  const handleSendRequest = async (): Promise<void> => {
    setRequestLoading(true);
    await onSendRequestClick();
    setRequestLoading(false);
  };

  return (
    <div className="good-sidebar">
      {((good?.discountValue ?? 0) > 0 || diff > 0) && (
        <div className="mb-4 flex align-center gap-3">
          <GoodDiscount discount={good?.discountValue} />

          {diff > 0 && (
            <span className="ff-regular fs-s color-bright-green" title={formattedDiff}>{`Выгода ${formattedDiff}`}</span>
          )}
        </div>
      )}

      <div className="mb-4 flex align-end wrap">
        <span className="mr-3 ff-medium fs-xl" title={formattedPriceToDisplay}>
          {formattedPriceToDisplay}
        </span>

        <span className="inline-flex align-end text-nowrap">
          {diff > 0 && (
            <span className="mr-2 ff-regular fs-l color-grey text-decoration-through" title={formattedOriginalPriceToDisplay}>
              {formattedOriginalPriceToDisplay}
            </span>
          )}
          <span className="mr-2 ff-regular fs-l color-grey">/</span>
          <span className="ff-regular fs-s color-dark-grey">шт</span>
        </span>
      </div>

      {isCustomer && good && (
        <div>
          {good.isAvailableForCustomer ? (
            <div>
              {!isSelfRegistered && (
                <div className="mb-6 ff-regular fs-s color-dark-grey" title={formattedPriceWithTaxes}>
                  {`сумма с НДС ${formattedPriceWithTaxes}`}
                </div>
              )}

              <div className="mb-4">
                <Counter count={count} disabled={updatingGoodsCountRequestsCount >= 2} onChange={changeCount} />
              </div>

              <div className="mb-6">
                {goodInBasket ? (
                  <Button className="button-lg success w-full" loading={isUpdatingGoodsLoading} onClick={onNavigateToBasketClick}>
                    Перейти в корзину
                  </Button>
                ) : (
                  <Button className="button-lg primary w-full" loading={loading} onClick={handleAdToBasket}>
                    Добавить в корзину
                  </Button>
                )}
              </div>

              <div className="mb-4">
                <GoodInStock remains={good?.remains} />
              </div>

              <div>
                <DeliveryBadge remains={good?.remains} count={count} />
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-4 flex align-center goods-sidebar-agreement">
                <Email className="icon-email-yellow" />
                <span className="mx-3 ff-medium fs-xs color-yellow">По согласованию</span>
                <Tooltip
                  overlayClassName="redesign"
                  overlayStyle={{ width: 232 }}
                  title="Товар доступен по запросу. Вы можете отправить автоматический запрос на согласование поставщику через кнопку ниже. С вами свяжутся по контактам, указанным в вашем профиле."
                >
                  <InfoYellow className="icon-info-yellow" />
                </Tooltip>
              </div>

              <div className="mb-6">
                <Button className="button-lg secondary w-full" loading={requestLoading} onClick={handleSendRequest}>
                  Отправить запрос
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isCustomer && (isAccountAdmin || isSellerManager) && !!settings.length && (
        <Dropdown
          overlayClassName="redesign"
          trigger={['click']}
          open={openSettings}
          onOpenChange={(open) => {
            if (openGoodsAvailabilitySettingsModal) {
              return;
            }

            setOpenSettings(open);
          }}
          dropdownRender={() => {
            return (
              <div className="custom-dropdown" style={{ width: 640 }}>
                <div
                  className={`custom-dropdown__scrollable scrollbar ${settings.length > ITEMS_FOR_VIEW_LIMIT_4 ? 'pr-4' : ''}`}
                >
                  {settings.map((item, index, array) => {
                    return (
                      <div key={index}>
                        <div className="flex align-center gap-4">
                          <IconBackground content={<Contract className="icon-contract-dark-grey" />} color={EColor.LightGrey} />

                          <div className="flex-1">
                            <div className="mb-2 ff-medium fs-s ellipsis" title={item.buyerName}>
                              {item.buyerName}
                            </div>

                            <div className="ellipsis">
                              {item.price !== null && (
                                <span className="ff-regular fs-xs color-dark-grey">{priceFormatter(item.price)}</span>
                              )}
                              {item.isAvailableForCustomer && (
                                <>
                                  {item.price !== null && <span className="ff-regular fs-xs color-dark-grey"> • </span>}
                                  <span className="ff-regular fs-xs color-dark-grey">Доступен для заказа</span>
                                </>
                              )}
                              {item.isManuallySet && (
                                <>
                                  {(item.price !== null || item.isAvailableForCustomer) && (
                                    <span className="ff-regular fs-xs color-dark-grey"> • </span>
                                  )}
                                  <span className="ff-regular fs-xs color-bright-green">Изменено</span>
                                </>
                              )}
                            </div>
                          </div>

                          <Button
                            className="button-sm secondary"
                            onClick={() => {
                              setSettingsItem(item);
                              setOpenGoodsAvailabilitySettingsModal(true);
                            }}
                            disabled={item.contractHasCustomFeed || item.inProgress}
                          >
                            Настроить
                          </Button>
                        </div>

                        {index !== array.length - 1 && <Divider marginY={8} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        >
          <div className="p-4 flex align-center gap-4 cursor-pointer good-sidebar-settings">
            <Image src={good?.images?.[0]} size={60} />

            <div className="flex-1">
              <div className="mb-3 ff-regular fs-s color-dark-grey">Настройки для контрактов</div>

              <span className="ff-medium fs-s">Цена и доступность</span>
            </div>
          </div>
        </Dropdown>
      )}

      <GoodsAvailabilitySettingsModal
        open={openGoodsAvailabilitySettingsModal}
        settings={settingsItem}
        onClose={() => setOpenGoodsAvailabilitySettingsModal(false)}
      />
    </div>
  );
};
